// IndexedDB-backed queue for run-mode captures made while offline (Task 4,
// docs/superpowers/plans/2026-08-06-offline-run-mode.md). One database,
// "weightogo-offline", one object store, "captures". Each record:
//
//   {
//     id:          auto-incrementing primary key — also the enqueue/replay
//                  order (see offline_controller.js's replay loop, which
//                  relies on getAll() returning ascending-by-key order),
//     url:         the form's submission target,
//     method:      the real HTTP verb to replay with ("PATCH"/"POST"),
//     formEntries: [[name, value], ...] — a serialized FormData. A File
//                  value becomes {blob, filename, type} (see
//                  offline_controller.js's serializeFormData/rebuildFormData
//                  for the round-trip; IndexedDB's structured-clone
//                  algorithm stores Blob/File objects natively, so `blob`
//                  survives a reload untouched),
//     csrf:        the CSRF token read from the page's meta tag at capture
//                  time (replays send it as X-CSRF-Token; see the spec's
//                  "CSRF on replay" note),
//     stageKey:    this capture's run_stage value, so a failed replay's
//                  banner can link back to the right stage,
//     queuedAt:    Date.now() at capture time, for display/debugging only —
//                  `id` (not this) is what determines replay order.
//   }
//
// This module owns nothing but the store: open/read/write/delete. No
// business logic (serialization, replay, banner text) lives here — that's
// offline_controller.js's job. Every export opens its own connection rather
// than sharing one long-lived handle: IndexedDB connections are cheap, and
// the queue must keep working correctly across a page reload (the whole
// point of persisting to IndexedDB instead of memory), so nothing here may
// assume a singleton survives across loads.

const DB_NAME = "weightogo-offline"
const DB_VERSION = 1
const STORE_NAME = "captures"

// Opens (and, on first use, creates) the database + store.
function openDatabase() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)

    request.onupgradeneeded = () => {
      const db = request.result
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        // autoIncrement gives every record an ordered `id` for free — that
        // ordering IS the capture order the spec requires replay to honor.
        db.createObjectStore(STORE_NAME, { keyPath: "id", autoIncrement: true })
      }
    }

    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

// Wraps a single IDBRequest in a promise.
function requestToPromise(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

// Opens a transaction on the captures store, runs `operation(store)`
// against it, and resolves once the TRANSACTION commits (not merely once
// `operation`'s own request succeeds) — that's the actual guarantee that
// the write landed on disk, which matters for a queue that must survive an
// unplanned reload moments later.
async function runTransaction(mode, operation) {
  const db = await openDatabase()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, mode)
    const store = tx.objectStore(STORE_NAME)
    let result
    Promise.resolve(operation(store))
      .then((value) => { result = value })
      .catch(reject)
    tx.oncomplete = () => resolve(result)
    tx.onerror = () => reject(tx.error)
    tx.onabort = () => reject(tx.error)
  })
}

// Adds a new capture, stamping queuedAt if the caller didn't. Resolves with
// the new record's id.
export function enqueue(record) {
  const withTimestamp = { ...record, queuedAt: record.queuedAt || Date.now() }
  return runTransaction("readwrite", (store) => requestToPromise(store.add(withTimestamp)))
}

// Every queued capture, oldest first (ascending by auto-incrementing id —
// see the ordering note on `id` above).
export function all() {
  return runTransaction("readonly", (store) => requestToPromise(store.getAll()))
}

// Drains a successfully-replayed capture.
export function remove(id) {
  return runTransaction("readwrite", (store) => requestToPromise(store.delete(id)))
}

// Total queued captures (queued + flagged-with-error alike — a flagged
// capture is still queued, per the spec's "stays queued, never silently
// dropped"). Cheaper than all().length when the caller doesn't need the
// records themselves (e.g. the sign-out confirm in sign_out_controller.js).
export function count() {
  return runTransaction("readonly", (store) => requestToPromise(store.count()))
}

// Wipes every queued capture, replayed or not. Used by sign_out_controller.js
// (final review M2) as a best-effort client-side companion to the
// Clear-Site-Data response header sent alongside sign-out — see that
// controller's clearCache for why a header alone isn't treated as enough
// once the operator has already confirmed queued captures may be lost.
export function clearAll() {
  return runTransaction("readwrite", (store) => requestToPromise(store.clear()))
}

// Flags a capture that failed to replay (4xx/5xx response) with a message,
// WITHOUT removing it — a failed replay stays in the queue so the next
// online event or 30s retry tries it again, and so the operator can see
// what needs attention rather than losing it silently.
export function markError(id, message) {
  return runTransaction("readwrite", (store) => {
    return requestToPromise(store.get(id)).then((record) => {
      if (!record) return
      record.error = message
      return requestToPromise(store.put(record))
    })
  })
}
